import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function OxfordPage() {
  return (
    <>
      <LocalBusinessSchema city="Oxford" latitude={51.752} longitude={-1.2577} />
      <PageHero
        title="Fractional CISO Services in Oxford"
        subtitle="Cybersecurity leadership for the Oxfordshire innovation ecosystem"
        eyebrow="Oxford"
        seoTitle="Fractional CISO Oxford | The CISO Network"
        seoDescription="Expert fractional CISO services for Oxford businesses. Access senior cybersecurity leadership for Oxfordshire's tech and research sectors."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing Oxford's Research and Innovation Economy
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Oxford represents one of the world's most concentrated centres of research and
                  innovation, with a unique ecosystem spanning artificial intelligence,
                  biotechnology, advanced materials, and quantum computing. The Oxford Science Park
                  and neighbouring Milton Park host hundreds of companies translating world-leading
                  research into commercial success.
                </p>
                <p>
                  Oxfordshire businesses face sophisticated cybersecurity challenges. AI and quantum
                  computing firms work on technologies that attract nation-state interest. Biotech
                  companies handle sensitive clinical and genomic data requiring exceptional
                  protection. Meanwhile, the region's space technology and satellite firms face
                  targeted threats against critical infrastructure.
                </p>
                <p>
                  Many Oxford businesses are university spin-outs or research-driven startups
                  serving global markets. These companies need credible security leadership to
                  satisfy investors, protect valuable IP, and meet the expectations of enterprise
                  clients - but often can't justify a full-time CISO in their early stages. Our
                  fractional CISO service provides Oxford businesses with the strategic security
                  expertise they need to commercialise breakthrough research securely.
                </p>
                <p>
                  Oxford's research institutions and life sciences cluster handle data and IP of
                  extraordinary value. The city's businesses — from university spinouts to
                  established research organisations — must balance open academic collaboration with
                  robust security. A fractional CISO who understands this tension can build controls
                  that protect without stifling the research culture that makes Oxford unique.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands the unique dynamics of the Oxfordshire
                      innovation ecosystem, from spin-outs to scale-ups.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Oxfordshire Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Oxford, the Science Park, Milton Park, and the
                      wider Thames Valley.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Oxford Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Research-grade security leadership for Oxfordshire innovators
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Research IP Protection',
                description:
                  'Secure breakthrough technologies and valuable research data from sophisticated threats.',
              },
              {
                icon: Target,
                title: 'Investor Confidence',
                description:
                  'Security programmes that satisfy venture capital due diligence requirements.',
              },
              {
                icon: Users,
                title: 'Spin-Out Specialists',
                description:
                  'CISOs experienced in guiding university research to commercial success.',
              },
              {
                icon: Clock,
                title: 'Growth Aligned',
                description:
                  'Flexible engagement that scales from seed stage through Series B and beyond.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Oxford - FAQs"
        subtitle="Common questions from Oxford and Oxfordshire businesses"
        items={[
          {
            id: 'oxford-1',
            question: 'How does a fractional CISO work with businesses in Oxford?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Oxford. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing research, life sciences, and deep tech companies.',
          },
          {
            id: 'oxford-2',
            question: 'What size of business benefits from a fractional CISO in Oxford?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Oxford startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'oxford-3',
            question: 'What industries do your CISOs cover in Oxford?',
            answer:
              "Our network includes CISOs with experience in research, life sciences, and deep tech. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'oxford-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'oxford-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Oxford Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can protect your
            research-driven innovation.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
